import { formatDate, isValidDate, nodeContainsDescendant, parseYearMonthDayDateString } from '@/helpers/daterange';
import { DateRange } from '@/types';
import {
  BlockStack,
  Box,
  Button,
  DatePicker,
  Icon,
  InlineGrid,
  InlineStack,
  OptionList,
  Popover,
  Scrollable,
  Select,
  TextField,
  useBreakpoints,
} from '@shopify/polaris';
import { ArrowRightIcon, CalendarIcon } from '@shopify/polaris-icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TextFieldWrapper } from './styled';

interface Range {
  title: string;
  alias: string;
  period: {
    since: Date;
    until: Date;
  };
}

interface DateRangePickerProps {
  range: DateRange;
  updateDateRange: (dateRange: DateRange) => void;
  datePresets: Range[];
  disabledFeature?: boolean;
  disableDatesBefore?: Date;
  disableDatesAfter?: Date;
  defaultText?: string;
  defaultDatePresetChoice?: Range;
}

function DateRangePicker({
  range,
  updateDateRange,
  datePresets,
  disabledFeature,
  disableDatesBefore,
  disableDatesAfter,
  defaultText,
  defaultDatePresetChoice,
}: DateRangePickerProps) {
  const { mdDown } = useBreakpoints();
  const today = useMemo(() => new Date(new Date().setHours(0, 0, 0, 0)), []);
  const [dateRange, setDateRange] = useState<DateRange>({
    since: range.since,
    until: range.until,
  });

  const [popoverActive, setPopoverActive] = useState<boolean>(false);

  const [activeDateRange, setActiveDateRange] = useState<Range>(() => {
    if (defaultText) {
      return dateRange.since
        ? {
            title: 'Custom',
            alias: 'custom',
            period: {
              since: typeof dateRange.since === 'string' ? new Date(dateRange.since) : dateRange.since,
              until: typeof dateRange.until === 'string' ? new Date(dateRange.until) : dateRange.until,
            },
          }
        : datePresets[0];
    } else {
      return defaultDatePresetChoice || datePresets[0];
    }
  });

  const [appliedDateRange, setAppliedDateRange] = useState<Range>(() => {
    if (defaultText) {
      return dateRange.since
        ? {
            title: 'Custom',
            alias: 'custom',
            period: {
              since: typeof dateRange.since === 'string' ? new Date(dateRange.since) : dateRange.since,
              until: typeof dateRange.until === 'string' ? new Date(dateRange.until) : dateRange.until,
            },
          }
        : datePresets[0];
    } else {
      return defaultDatePresetChoice || datePresets[0];
    }
  });

  const [inputValues, setInputValues] = useState({ since: '', until: '' });

  const [{ month, year }, setDate] = useState({
    month:
      typeof activeDateRange.period.since === 'string'
        ? new Date(activeDateRange.period.since).getMonth()
        : activeDateRange.period.since.getMonth(),
    year:
      typeof activeDateRange.period.since === 'string'
        ? new Date(activeDateRange.period.since).getFullYear()
        : activeDateRange.period.since.getFullYear(),
  });

  const datePickerRef = useRef(null);

  const isNodeWithinPopover = (node: Node) => {
    return datePickerRef?.current ? nodeContainsDescendant(datePickerRef.current, node) : false;
  };

  const handleStartInputValueChange = (value: string) => {
    setInputValues((prevState) => {
      return { ...prevState, since: value };
    });

    if (isValidDate(value)) {
      const newSince = parseYearMonthDayDateString(value);
      setActiveDateRange((prevState) => {
        const newPeriod =
          prevState.period && newSince <= prevState.period.until
            ? { since: newSince, until: prevState.period.until }
            : { since: newSince, until: newSince };
        return {
          ...prevState,
          period: newPeriod,
        };
      });
      setDateRange({
        since: formatDate(newSince),
        until: formatDate(activeDateRange.period.until),
      });
    }
  };

  const handleEndInputValueChange = (value: string) => {
    setInputValues((prevState) => ({ ...prevState, until: value }));
    if (isValidDate(value)) {
      const newUntil = parseYearMonthDayDateString(value);
      setActiveDateRange((prevState) => {
        const newPeriod =
          prevState.period && newUntil >= prevState.period.since
            ? { since: prevState.period.since, until: newUntil }
            : { since: newUntil, until: newUntil };
        return {
          ...prevState,
          period: newPeriod,
        };
      });
      setDateRange({
        since: formatDate(activeDateRange.period.since),
        until: formatDate(newUntil),
      });
    }
  };

  const handleInputBlur = (event?: React.FocusEvent<Element>) => {
    if (!event) {
      setPopoverActive(false);
      return;
    }

    const { relatedTarget } = event;

    const isRelatedTargetWithinPopover = relatedTarget != null && isNodeWithinPopover(relatedTarget as Node);
    if (isRelatedTargetWithinPopover) {
      return;
    }

    // setPopoverActive(false);
  };

  const handleMonthChange = (month: number, year: number) => {
    setDate({ month, year });
  };

  function isSameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }

  function isSameRange(a: Range['period'], b: { since: Date; until: Date }): boolean {
    return isSameDay(a.since, b.since) && isSameDay(a.until, b.until);
  }

  function handleCalendarChange({ start, end }: { start: Date; end: Date }) {
    const newDateRange = datePresets.find((range) => isSameRange(range.period, { since: start, until: end })) || {
      alias: 'custom',
      title: 'Custom',
      period: { since: start, until: end },
    };

    setActiveDateRange(newDateRange);
    setInputValues({
      since: formatDate(start),
      until: formatDate(end),
    });
    setDateRange({
      since: formatDate(start),
      until: formatDate(end),
    });
  }
  function apply() {
    const matchedPreset = datePresets.find((preset) => isSameRange(preset.period, activeDateRange.period));

    const finalRange = matchedPreset
      ? matchedPreset
      : {
          ...activeDateRange,
          title: 'Custom',
          alias: 'custom',
        };
    setAppliedDateRange(finalRange);
    setPopoverActive(false);
    updateDateRange(dateRange);
  }
  function cancel() {
    setPopoverActive(false);
  }

  useEffect(() => {
    if (!dateRange.since) {
      setDateRange({
        since: formatDate(today),
        until: formatDate(today),
      });
    }
  }, [dateRange.since, today]);

  useEffect(() => {
    if (activeDateRange) {
      const since =
        typeof activeDateRange.period.since === 'string' ? new Date(activeDateRange.period.since) : activeDateRange.period.since;
      const until =
        typeof activeDateRange.period.until === 'string' ? new Date(activeDateRange.period.until) : activeDateRange.period.until;
      setInputValues({
        since: formatDate(since),
        until: formatDate(until),
      });

      // const monthDifference = monthDiff(
      //   { year, month },
      //   {
      //     year: activeDateRange.period.until.getFullYear(),
      //     month: activeDateRange.period.until.getMonth(),
      //   },
      // );
      // if (monthDifference > 1 || monthDifference < 0) {
      //   setDate({
      //     month: activeDateRange.period.until.getMonth(),
      //     year: activeDateRange.period.until.getFullYear(),
      //   });
      // }
    }
  }, [activeDateRange, month, year]);

  const getButtonLabel = useCallback(() => {
    if (appliedDateRange.alias === 'custom') {
      return `${formatDate(appliedDateRange.period.since)} – ${formatDate(appliedDateRange.period.until)}`;
    }
    return appliedDateRange.title || 'Select date range';
  }, [appliedDateRange.alias, appliedDateRange.period.since, appliedDateRange.period.until, appliedDateRange.title]);

  return (
    <Popover
      active={popoverActive}
      autofocusTarget="none"
      preferredAlignment="left"
      preferredPosition="below"
      fluidContent
      sectioned={false}
      fullHeight
      activator={
        <Button icon={CalendarIcon} disabled={disabledFeature} onClick={() => setPopoverActive(!popoverActive)}>
          {defaultText || getButtonLabel()}
        </Button>
      }
      onClose={() => setPopoverActive(false)}
    >
      <Popover.Pane fixed>
        <InlineGrid
          columns={{
            xs: '1fr',
            md: 'max-content max-content',
          }}
          gap="0"
        >
          <Box
            maxWidth={mdDown ? '516px' : '212px'}
            width={mdDown ? '100%' : '212px'}
            padding={{ xs: '500', md: '0' }}
            paddingBlockEnd={{ xs: '100', md: '0' }}
          >
            {mdDown ? (
              <Select
                label="dateRangeLabel"
                labelHidden
                onChange={(value) => {
                  const result = datePresets.find(({ title, alias }) => title === value || alias === value);
                  if (result) {
                    setActiveDateRange(result);
                    setDateRange({
                      since: formatDate(result.period.since),
                      until: formatDate(result.period.until),
                    });
                  }
                }}
                value={activeDateRange?.title || activeDateRange?.alias || ''}
                options={datePresets.map(({ alias, title }) => title || alias)}
              />
            ) : (
              <Scrollable height={'344px'}>
                <OptionList
                  options={datePresets.map((range) => ({
                    value: range.alias,
                    label: range.title,
                  }))}
                  selected={[activeDateRange.alias]}
                  onChange={(value) => {
                    const result = datePresets.find((range) => range.alias === value[0]);
                    if (result) {
                      setActiveDateRange(result);
                      setDateRange({
                        since: formatDate(result.period.since),
                        until: formatDate(result.period.until),
                      });
                    }
                  }}
                />
              </Scrollable>
            )}
          </Box>
          <Box padding={{ xs: '500' }} maxWidth={mdDown ? '320px' : '516px'}>
            <BlockStack gap="400">
              <InlineStack gap="200">
                <TextFieldWrapper>
                  <TextField
                    role="combobox"
                    label={'Since'}
                    labelHidden
                    prefix={<Icon source={CalendarIcon} />}
                    value={inputValues.since}
                    onChange={handleStartInputValueChange}
                    onBlur={handleInputBlur}
                    autoComplete="off"
                  />
                </TextFieldWrapper>
                <Icon source={ArrowRightIcon} />
                <TextFieldWrapper>
                  <TextField
                    role="combobox"
                    label={'Until'}
                    labelHidden
                    prefix={<Icon source={CalendarIcon} />}
                    value={inputValues.until}
                    onChange={handleEndInputValueChange}
                    onBlur={handleInputBlur}
                    autoComplete="off"
                  />
                </TextFieldWrapper>
              </InlineStack>
              <div>
                <DatePicker
                  month={month}
                  year={year}
                  selected={{
                    start: activeDateRange.period.since,
                    end: activeDateRange.period.until,
                  }}
                  onMonthChange={handleMonthChange}
                  onChange={handleCalendarChange}
                  multiMonth={true}
                  allowRange
                  disableDatesBefore={disableDatesBefore}
                  disableDatesAfter={disableDatesAfter}
                />
              </div>
            </BlockStack>
          </Box>
        </InlineGrid>
      </Popover.Pane>
      <Popover.Pane fixed>
        <Popover.Section>
          <InlineStack align="end" gap="200">
            <Button onClick={cancel}>Cancel</Button>
            <Button variant="primary" onClick={apply}>
              Apply
            </Button>
          </InlineStack>
        </Popover.Section>
      </Popover.Pane>
    </Popover>
  );
}

export default DateRangePicker;
